import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { packeRaster, spaltenFuer } from '../../../lib/immobilien/rasterPacken';
import type { WidgetGroesse } from './Widget';

/**
 * Widget-Raster (README §4.6): Spalten wie repeat(auto-fill, minmax(150px, 1fr)),
 * Zeilen 158 px, Abstand 16 px. Statt reinem CSS-„dense“ wird gepackt: Widgets wachsen in
 * freie Nachbarzellen, damit neben und unter großen Widgets keine Lücken bleiben.
 */
export interface RasterElement { key: string; groesse: WidgetGroesse; inhalt: React.ReactNode }

const MASS: Record<WidgetGroesse, { w: number; h: number }> = {
  normal: { w: 1, h: 1 }, breit: { w: 2, h: 1 }, hoch: { w: 1, h: 2 }, gross: { w: 2, h: 2 },
};

/** Tatsächliche Größe eines Widgets im gepackten Raster (Spalten × Zeilen). */
export const RasterMassKontext = createContext<{ w: number; h: number } | null>(null);
export function useRasterMass(): { w: number; h: number } | null {
  return useContext(RasterMassKontext);
}

const ABSTAND = 16;
const ZEILE = 158;

export function WidgetGrid({ elemente, className = '', minSpalte = 150, zeile = ZEILE, abstand = ABSTAND }: {
  elemente: readonly RasterElement[];
  className?: string;
  /** Mindestbreite einer Spalte (Mieter-Detail: 160 px) */
  minSpalte?: number;
  /** Zeilenhöhe (Mieter-Detail: 150 px) */
  zeile?: number;
  /** Abstand (Mieter-Detail: 14 px) */
  abstand?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [spalten, setSpalten] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const messen = () => setSpalten(spaltenFuer(el.clientWidth, minSpalte, abstand));
    messen();
    const ro = new ResizeObserver(messen);
    ro.observe(el);
    return () => ro.disconnect();
  }, [minSpalte, abstand]);

  const plaetze = useMemo(
    () => (spalten ? packeRaster(elemente.map(e => ({ key: e.key, ...MASS[e.groesse] })), spalten) : null),
    [elemente, spalten],
  );

  return (
    <div
      ref={ref}
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: spalten ? `repeat(${spalten}, minmax(0, 1fr))` : `repeat(auto-fill, minmax(${minSpalte}px, 1fr))`,
        gridAutoRows: zeile,
        gap: abstand,
        gridAutoFlow: 'dense',
      }}
    >
      {elemente.map(e => {
        const p = plaetze?.find(x => x.key === e.key);
        const m = p ? { w: p.w, h: p.h } : MASS[e.groesse];
        return (
          <div key={e.key} className="grid min-w-0"
            style={p ? { gridColumn: `${p.spalte + 1} / span ${p.w}`, gridRow: `${p.zeile + 1} / span ${p.h}` } : { gridColumn: `span ${m.w}`, gridRow: `span ${m.h}` }}>
            <RasterMassKontext.Provider value={m}>{e.inhalt}</RasterMassKontext.Provider>
          </div>
        );
      })}
    </div>
  );
}
